import { useEffect, useState, useCallback, useRef, ReactElement, RefObject } from 'react';
import DataGrid, {
    StateStoring,
    Paging,
    Column,
    Pager,
    Summary,
    TotalItem,
    MasterDetail,
    Selection,
    KeyboardNavigation,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import 'devextreme/data/odata/store';
import { v4 as uuidv4 } from 'uuid';
import { UsersApi } from '@apply/api';
import { RefreshEvents } from '../events/RefreshEvents';
import { GridSettingType } from '@apply/constants';
import dxDataGrid, {
    CellClickEvent,
    CellDblClickEvent,
    CellPreparedEvent,
    Column as DevExtremeColumn,
    InitNewRowEvent,
    RowDblClickEvent,
    RowExpandingEvent,
    RowPreparedEvent,
    RowRemovedEvent,
} from 'devextreme/ui/data_grid';
import { EventInfo } from 'devextreme/events';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

export { Column, Summary, TotalItem, CustomStore, MasterDetail };

interface BfiDataGridProps {
    dataGridRef?: RefObject<DataGrid | null>;
    applicationInsights?: ApplicationInsights;
    children?: unknown;
    gridType?: GridSettingType;
    dataStore?: CustomStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataSource?: any;
    allowedPageSizes?: number[];
    defaultPageSize?: number;
    onCellPrepared?: (e: CellPreparedEvent<unknown, unknown>) => void;
    onSelectionChanged?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    onCellClick?: (e: CellClickEvent<unknown, unknown>) => void;
    onCellDblClick?: (e: CellDblClickEvent<unknown, unknown>) => void;
    onRowDblClick?: (e: RowDblClickEvent<unknown, unknown>) => void;
    customizeColumns?: (columns: DevExtremeColumn<unknown, unknown>[]) => void;
    onContentReady?: (e: EventInfo<dxDataGrid<unknown, unknown>>) => void;
    allowColumnReordering?: boolean;
    onRowPrepared?: (e: RowPreparedEvent<unknown, unknown>) => void;
    onRowRemoved?: (e: RowRemovedEvent<unknown, unknown>) => void;
    onRowExpanding?: (e: RowExpandingEvent<unknown, unknown>) => void;
    onInitNewRow?: (e: InitNewRowEvent<unknown, unknown>) => void;
    wordWrapEnabled?: boolean;
    className?: string;
    width?: number;
    keyExpr?: string;
    allowColumnResizing?: boolean;
    pagerVisible?: boolean;
    showColumnHeaders?: boolean;
    leadPanelEnabled?: boolean;
    loadPanelEnabled?: boolean;
    showBorders?: boolean;
    showRowLines?: boolean;
    rowAlternationEnabled?: boolean;
    selectionMode?: string;
    remoteOperations?: boolean;
    noDataText?: string;
    height?: any;
}

export const BfiDataGrid = ({
    dataGridRef,
    applicationInsights,
    children,
    gridType,
    dataStore,
    dataSource,
    onCellPrepared,
    onSelectionChanged,
    onCellClick,
    onCellDblClick,
    onRowDblClick,
    customizeColumns,
    onContentReady,
    onRowPrepared,
    onRowRemoved,
    onRowExpanding,
    onInitNewRow,
    className,
    width,
    keyExpr = 'Id',
    allowColumnReordering = true,
    allowedPageSizes = [15, 25, 50],
    defaultPageSize = 25,
    allowColumnResizing = true,
    wordWrapEnabled = false,
    pagerVisible = true,
    showColumnHeaders = true,
    loadPanelEnabled = true,
    showBorders = true,
    showRowLines = true,
    rowAlternationEnabled = true,
    selectionMode = 'single',
    remoteOperations = true,
    noDataText = '',
    height,
}: BfiDataGridProps): ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataGrid = useRef<any>(null);
    //const usersApi = useMemo(() => new UsersApi.UsersApi(applicationInsights), [applicationInsights]);
    const usersApi = new UsersApi.UsersApi(applicationInsights);

    //Used for Table refresh purposes (useRef doesn't work reliable with the DataGrid)
    const [tableId] = useState(uuidv4());
    const _afterRefresh = () => {
        const grid = dataGrid?.current?.instance;
        if (grid !== null && grid !== undefined) {
            grid.collapseAll(-1); //So that Master/Detail sectioon is not rendered funny if a row is deleted.
            grid.refresh();
        }
    };

    const setRef = (ref) => {
        if (ref) {
            dataGrid.current = ref;
            if (dataGridRef) dataGridRef.current = ref;
        }
    };

    useEffect(() => {
        const refreshEvents = new RefreshEvents();
        refreshEvents.addRefreshListener(_afterRefresh);

        return () => {
            refreshEvents.removeRefreshListener(_afterRefresh);
        };
    }, []);

    const loadState = async () => {
        if (!gridType) return null;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore, to ignore warning that gridType cannot be null (a guard clause above prevents this)
        const data = await usersApi.loadUserGridSetting(gridType);
        if (data) {
            const state = JSON.parse(data);
            state.pageIndex = 0;
            return state;
        } else {
            return null;
        }
    };

    const saveState = useCallback(async (state) => {
        if (!gridType || !usersApi) return;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore, to ignore warning that gridType cannot be null (a guard clause above prevents this)
        await usersApi.saveUserGridSetting({
            gridTypeId: gridType,
            storageKey: JSON.stringify(state),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <DataGrid
            allowColumnReordering={allowColumnReordering}
            allowColumnResizing={allowColumnResizing}
            cellHintEnabled={true}
            className={className ? className : ''}
            columnAutoWidth={false}
            columnHidingEnabled={false}
            columnMinWidth={20}
            columnResizingMode={'nextColumn'}
            customizeColumns={customizeColumns}
            dataSource={dataSource ?? dataStore}
            id={tableId}
            keyExpr={dataSource ? keyExpr : ''}
            loadPanel={{ enabled: loadPanelEnabled }}
            onCellClick={onCellClick}
            onCellDblClick={onCellDblClick}
            onRowDblClick={onRowDblClick}
            onCellPrepared={onCellPrepared}
            onContentReady={onContentReady}
            onRowPrepared={onRowPrepared}
            onRowRemoved={onRowRemoved}
            onRowExpanding={onRowExpanding}
            onInitNewRow={onInitNewRow}
            onSelectionChanged={onSelectionChanged}
            ref={(ref) => setRef(ref)}
            remoteOperations={remoteOperations}
            repaintChangesOnly={true}
            rowAlternationEnabled={rowAlternationEnabled}
            showBorders={showBorders}
            showColumnHeaders={showColumnHeaders}
            showRowLines={showRowLines}
            width={width}
            wordWrapEnabled={wordWrapEnabled}
            noDataText={noDataText}
            height={height}
        >
            <KeyboardNavigation
                editOnKeyPress={true}
                enterKeyAction='startEdit'
                enterKeyDirection='column'
            />
            <Selection mode={selectionMode} />
            <StateStoring
                enabled={true}
                type='custom'
                customLoad={loadState}
                customSave={saveState}
            />
            {children}
            <Paging
                defaultPageSize={defaultPageSize}
                enabled={defaultPageSize > 0}
            />
            {defaultPageSize > 0 && (
                <Pager
                    showPageSizeSelector={allowedPageSizes.length > 1}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                    showInfo={true}
                    visible={pagerVisible}
                />
            )}
        </DataGrid>
    );
};
